import React from "react"
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core"

import {
  customEventPropertyTypes,
  CustomEventProperty,
  CustomEventPropertyType,
  CustomEventPropertyTypeName,
} from "@root/store/types"
import SnackContext from "./Snackbar"
import CustomEventPropertiesTable from "./CustomEventPropertiesTable"

export interface Props {
  properties: CustomEventProperty[]
  onAddProperty(property: CustomEventProperty): void
  onRemoveProperty(property: CustomEventProperty): void
}

const useStyles = makeStyles({
  button: {
    alignSelf: "center",
  },
})

const CustomEventProperties: React.FC<Props> = ({
  properties,
  onAddProperty,
  onRemoveProperty,
}) => {
  const classes = useStyles()
  const [key, setKey] = React.useState("")
  const [value, setValue] = React.useState("")
  const [type, setType] = React.useState<CustomEventPropertyType>("string")
  const { createSnack } = React.useContext(SnackContext)

  return (
    <>
      <CustomEventPropertiesTable
        properties={properties}
        onRemoveProperty={onRemoveProperty}
      />

      <Typography variant="h6">Add New Property</Typography>
      <Box
        data-qa-selector="custom-event-property-form"
        component="form"
        onSubmit={(ev) => {
          ev.preventDefault()
          try {
            onAddProperty(makeProperty())
            setKey("")
            setValue("")
          } catch (e) {
            createSnack(e.message)
          }
        }}
      >
        <Grid container spacing={2}>
          <Grid item sm={2} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="property-type-label">Type</InputLabel>
              <Select
                data-qa-selector="property-type-select"
                labelId="property-type-label"
                id="property-type-id"
                value={type}
                onChange={(ev) =>
                  setType(ev.target.value as CustomEventPropertyType)
                }
              >
                {customEventPropertyTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {CustomEventPropertyTypeName[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextField
              required
              fullWidth
              data-qa-selector="key-input"
              id="key-id"
              label="Key"
              value={key}
              onChange={(ev) => setKey(ev.target.value)}
            />
          </Grid>

          <Grid item sm={4} xs={12}>
            {getInput()}
          </Grid>
          <Grid item sm={2} xs={12} className={classes.button}>
            <Button
              type="submit"
              fullWidth
              data-qa-selector="submit"
              variant="contained"
              disabled={!key || !value || !type}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  )

  function getInput(): React.ReactElement {
    switch (type) {
      case "boolean":
        return (
          <Checkbox
            data-qa-selector="value-checkbox"
            checked={value === "1"}
            onChange={(ev) => setValue(ev.target.checked ? "1" : "0")}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        )
      case "json":
      case "number":
      case "string":
        return (
          <TextField
            fullWidth
            data-qa-selector="value-input"
            id="value-id"
            label="Value"
            type={type === "number" ? "number" : "text"}
            multiline={type === "json"}
            value={value}
            onChange={(ev) => setValue(ev.target.value)}
          />
        )
      default:
        const msg: never = type
        throw new Error(`invalid type ${msg}`)
    }
  }

  function makeProperty(): CustomEventProperty {
    switch (type) {
      case "boolean":
        return {
          type,
          key,
          value: value === "1",
        }
      case "json":
        try {
          JSON.parse(value)
        } catch (e) {
          throw new Error(`invalid json: ${e.message}`)
        }
        return {
          type,
          key,
          value,
        }
      case "number":
        const num = Number(value)
        if (Number.isNaN(num)) {
          throw new Error("unable to parse number")
        }
        return {
          type,
          key,
          value: num,
        }
      case "string":
        return {
          type,
          key,
          value,
        }
      default:
        const msg: never = type
        throw new Error(`invalid type: ${msg}`)
    }
  }
}

export default CustomEventProperties
